import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as svc from './cobrador-service';
import { cobradorKeys } from './keys';
import type { SeccionCobrador } from './cobrador-service';
import type { CercaUbicacion } from '../components/useCercaUbicacion';

export function useCobradorDashboard() {
  return useQuery({
    queryKey: cobradorKeys.dashboard(),
    queryFn: () => svc.getCobradorDashboard(),
    staleTime: 30_000,
  });
}

interface UseCobradorClienteContextOptions {
  enabled?: boolean;
}

export function useCobradorClienteContext(
  idCliente: number,
  options?: UseCobradorClienteContextOptions,
) {
  return useQuery({
    queryKey: cobradorKeys.clienteContext(idCliente),
    queryFn: () => svc.getCobradorClienteContext(idCliente),
    enabled: idCliente > 0 && (options?.enabled ?? true),
    staleTime: 30_000,
  });
}

export function useCobradorClientes(
  seccion: SeccionCobrador,
  params: { limit: number; offset: number },
) {
  return useQuery({
    queryKey: cobradorKeys.clientes(seccion, params),
    queryFn: () => svc.getCobradorClientes({ seccion, ...params }),
    staleTime: 30_000,
  });
}

/**
 * Tab Cerca: clientes pendientes con coordenadas dentro del radio. Solo se
 * consulta con la posición capturada (snapshot del navegador); sin ubicación
 * el query queda inactivo y no se dispara la petición.
 */
export function useCobradorCerca(ubicacion: CercaUbicacion | null, radioMetros: number) {
  const params =
    ubicacion !== null
      ? { latitud: ubicacion.lat, longitud: ubicacion.lng, radioMetros }
      : null;
  return useQuery({
    queryKey: cobradorKeys.cerca(params),
    queryFn: params
      ? () => svc.getCobradorCerca(params)
      : async () => {
          throw new Error('useCobradorCerca requiere la ubicación capturada del cobrador');
        },
    enabled: params !== null,
    staleTime: 30_000,
  });
}

/** Refresca dashboard + todas las secciones de la worklist tras registrar pago/visita. */
export function useInvalidarCobrador() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: cobradorKeys.all });
  };
}
